import React from 'react';

import { Box, Group, Text } from '@mantine/core';
import type { MovementWithSection } from 'components/Leaderboard/movementLabel';
import type { MovementResult, ResultsState } from 'components/Workout/logging';
import { WHITEBOARD_SIZES } from 'components/Workout/whiteboardDisplay';
import { WorkoutLevelEnum } from 'types/enums';
import type { AthleteKiloClassWithAthleteAndWaitlistOrder } from 'types/generated/models/AthleteKiloClassWithAthleteAndWaitlistOrder';

import WhiteboardCell from './WhiteboardCell';
import type { WhiteboardLevels } from '../hooks/useWhiteboardResults';

interface WhiteboardAthleteRowProps {
  row: AthleteKiloClassWithAthleteAndWaitlistOrder;
  movements: MovementWithSection[];
  rowResults: ResultsState | undefined;
  rowLevels: WhiteboardLevels[string] | undefined;
  onChange: (athleteKiloClassId: string, movementId: string, value: MovementResult) => void;
  onCommit: (
    athleteId: string,
    athleteKiloClassId: string,
    movementId: string,
    value: MovementResult,
  ) => Promise<void>;
  onLevelChange: (athleteKiloClassId: string, workoutSectionId: string, level: WorkoutLevelEnum) => void;
  onLevelCommit: (
    athleteId: string,
    athleteKiloClassId: string,
    workoutSectionId: string,
    level: WorkoutLevelEnum,
  ) => Promise<void>;
  enlarged?: boolean;
  isHighlighted?: boolean;
  onNameClick?: () => void;
}

const WhiteboardAthleteRow = ({
  row,
  movements,
  rowResults,
  rowLevels,
  onChange,
  onCommit,
  onLevelChange,
  onLevelCommit,
  enlarged = false,
  isHighlighted = false,
  onNameClick,
}: WhiteboardAthleteRowProps): JSX.Element => (
  <Box
    component='tr'
    data-athlete-id={row.athlete.athleteId}
    sx={theme => ({ borderBottom: `1px solid ${theme.colors.gray[2]}` })}
  >
    <Box
      component='td'
      px='md'
      py={enlarged ? 'md' : 'sm'}
      onClick={onNameClick}
      sx={theme => ({
        position: 'sticky',
        left: 0,
        background: isHighlighted ? theme.colors.green[0] : theme.white,
        zIndex: 1,
        borderRight: `1px solid ${theme.colors.gray[2]}`,
        verticalAlign: 'middle',
        cursor: onNameClick ? 'pointer' : undefined,
      })}
    >
      <Group spacing='xs' noWrap align='center'>
        <Box
          sx={theme => ({
            width: enlarged ? WHITEBOARD_SIZES.rowDot.large : WHITEBOARD_SIZES.rowDot.compact,
            height: enlarged ? WHITEBOARD_SIZES.rowDot.large : WHITEBOARD_SIZES.rowDot.compact,
            borderRadius: '50%',
            backgroundColor: theme.colors.gray[4],
            flexShrink: 0,
          })}
        />
        <Text
          size={enlarged ? WHITEBOARD_SIZES.resultsAthleteName : 'sm'}
          weight={500}
          sx={{
            lineHeight: 1.3,
            wordBreak: 'break-word',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
          }}
        >
          {row.athlete.fullName}
        </Text>
      </Group>
    </Box>
    {movements.map(movement => (
      <Box
        component='td'
        key={movement.workoutSectionMovementId}
        px='md'
        py={enlarged ? 'md' : 'sm'}
        // Top-align so every cell's content (level picker + scoring input) starts at the
        // same row, regardless of which column is tallest (e.g. a multi-set weight input) —
        // otherwise shorter cells get vertically centered and drift out of alignment.
        sx={theme => ({ borderLeft: `1px solid ${theme.colors.gray[2]}`, verticalAlign: 'top' })}
      >
        <WhiteboardCell
          movement={movement}
          value={rowResults?.[movement.workoutSectionMovementId]}
          onChange={value => onChange(row.athleteKiloClassId, movement.workoutSectionMovementId, value)}
          onCommit={value =>
            onCommit(row.athlete.athleteId, row.athleteKiloClassId, movement.workoutSectionMovementId, value)
          }
          enlarged={enlarged}
          hasLevels={movement.levelScaled || movement.levelRx || movement.levelRxPlus}
          level={rowLevels?.[movement.workoutSectionId] ?? null}
          levelScaled={movement.levelScaled}
          levelRx={movement.levelRx}
          levelRxPlus={movement.levelRxPlus}
          onLevelChange={level => onLevelChange(row.athleteKiloClassId, movement.workoutSectionId, level)}
          onLevelCommit={level =>
            onLevelCommit(row.athlete.athleteId, row.athleteKiloClassId, movement.workoutSectionId, level)
          }
        />
      </Box>
    ))}
  </Box>
);

export default WhiteboardAthleteRow;
